import {FC, useState} from 'react';
import {Alert, Modal, notification, Radio, Space, Tag, Typography} from 'antd';
import {ExclamationCircleOutlined} from '@ant-design/icons';
import {cancelJobSubscription} from '../../services/api.ts';
import {IJobSubscription} from '../../services/types.ts';

const {Text, Title} = Typography;

type CancelMode = 'end_of_period' | 'immediate';

interface CancelSubscriptionModalProps {
    subscription: IJobSubscription;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CancelSubscriptionModal: FC<CancelSubscriptionModalProps> = ({subscription, open, onClose, onSuccess}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCanceled, setIsCanceled] = useState(false);
    const [cancelMode, setCancelMode] = useState<CancelMode>('end_of_period');

    const formatDate = (timestamp: number | null) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const activeUntilDate = subscription.next_renewal ?? subscription.expiration_date;

    const handleCancel = async () => {
        setIsSubmitting(true);
        try {
            await cancelJobSubscription(subscription.id, cancelMode === 'immediate');
            setIsCanceled(true);
            notification.success({
                message: cancelMode === 'immediate'
                    ? 'Stellenanzeige deaktiviert'
                    : 'Abonnement gekündigt',
                description: cancelMode === 'immediate'
                    ? 'Ihre Stellenanzeige wurde sofort deaktiviert.'
                    : 'Ihr Abonnement wurde erfolgreich zum Ende der Laufzeit gekündigt.',
            });
            onSuccess();
        } catch (e) {
            console.error(e);
            notification.error({
                message: 'Fehler',
                description: 'Das Abonnement konnte nicht gekündigt werden.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isCanceled) {
        return (
            <Modal
                title="Kündigung bestätigt"
                open={open}
                onCancel={onClose}
                footer={null}
            >
                <Alert
                    message={cancelMode === 'immediate'
                        ? 'Ihre Stellenanzeige wurde sofort deaktiviert.'
                        : 'Ihr Abonnement wurde erfolgreich gekündigt.'
                    }
                    description={cancelMode === 'immediate'
                        ? 'Die Stellenanzeige ist ab sofort nicht mehr sichtbar für Kandidaten.'
                        : activeUntilDate
                            ? `Ihre Stellenanzeige bleibt aktiv bis zum ${formatDate(activeUntilDate)}.`
                            : 'Ihre Stellenanzeige bleibt bis zum Ende der aktuellen Abrechnungsperiode aktiv.'
                    }
                    type="success"
                    showIcon
                />
            </Modal>
        );
    }

    return (
        <Modal
            title={
                <Space>
                    <ExclamationCircleOutlined style={{color: '#faad14'}}/>
                    <span>Abonnement kündigen</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            onOk={handleCancel}
            okText="Ja, kündigen"
            cancelText="Abbrechen"
            okButtonProps={{danger: true, loading: isSubmitting}}
            width={520}
        >
            <Space direction="vertical" size="middle" style={{width: '100%'}}>
                <Alert
                    message="Achtung: Diese Aktion kann nicht rückgängig gemacht werden."
                    type="warning"
                    showIcon
                />

                <div>
                    <Text type="secondary">Stellenanzeige:</Text>
                    <Title level={5} style={{margin: 0}}>
                        {subscription.job_title ?? `Stelle #${subscription.job_demand_id}`}
                    </Title>
                </div>

                {subscription.active_addons.length > 0 && (
                    <div>
                        <Text type="secondary">Folgende Add-ons werden ebenfalls gekündigt:</Text>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4}}>
                            {subscription.active_addons.map(addon => (
                                <Tag key={addon.key} color="red">
                                    {addon.name} (€ {addon.price_monthly},-/Mo.)
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <Text type="secondary">Monatliche Kosten die entfallen:</Text>
                    <Text strong style={{display: 'block', fontSize: 18}}>
                        € {subscription.cost_monthly},-/Monat
                    </Text>
                </div>

                <div>
                    <Text type="secondary" style={{display: 'block', marginBottom: 8}}>
                        Wann soll die Stellenanzeige deaktiviert werden?
                    </Text>
                    <Radio.Group
                        value={cancelMode}
                        onChange={(e) => setCancelMode(e.target.value)}
                        style={{width: '100%'}}
                    >
                        <Space direction="vertical" style={{width: '100%'}}>
                            <Radio value="end_of_period" style={{whiteSpace: 'normal'}}>
                                <div>
                                    <Text strong>Zum Ende der Laufzeit</Text>
                                    <br/>
                                    <Text type="secondary" style={{fontSize: 13}}>
                                        {activeUntilDate
                                            ? `Die Anzeige bleibt aktiv bis zum ${formatDate(activeUntilDate)}. Es fallen keine weiteren Kosten an.`
                                            : 'Die Anzeige bleibt bis zum Ende der aktuellen Abrechnungsperiode aktiv.'
                                        }
                                    </Text>
                                </div>
                            </Radio>
                            <Radio value="immediate" style={{whiteSpace: 'normal'}}>
                                <div>
                                    <Text strong>Sofort deaktivieren</Text>
                                    <br/>
                                    <Text type="secondary" style={{fontSize: 13}}>
                                        Die Stellenanzeige wird sofort offline genommen und ist nicht mehr sichtbar für Kandidaten.
                                    </Text>
                                </div>
                            </Radio>
                        </Space>
                    </Radio.Group>
                </div>

                {cancelMode === 'immediate' && (
                    <Alert
                        message="Die Stellenanzeige wird sofort deaktiviert und ist nicht mehr für Kandidaten sichtbar."
                        type="error"
                        showIcon
                    />
                )}

                {cancelMode === 'end_of_period' && activeUntilDate && (
                    <div>
                        <Text type="secondary">Aktiv bis:</Text>
                        <Text strong style={{display: 'block'}}>
                            {formatDate(activeUntilDate)}
                        </Text>
                    </div>
                )}
            </Space>
        </Modal>
    );
};
